import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import LinearProgress from '@mui/material/LinearProgress'
import Pagination from '@mui/material/Pagination'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import Typography from '@mui/material/Typography'
import { useLayoutEffect, useRef, type KeyboardEvent, type ReactNode } from 'react'
import type { AssignedFarm, AssignedFarmsQuery, AssignedFarmsSortField } from '@/types/apiModels'
import { CONTRACT_STATUS_LABELS } from './contractStatusLabels'

export type FarmsTableProps = {
  farms: AssignedFarm[]
  page: number
  totalPages: number
  total: number
  pageSize: number
  sort: AssignedFarmsQuery['sort']
  // Changes exactly when page, sort or filters do, so each new result set starts at the top.
  queryKey: string
  onToggleSort: (field: AssignedFarmsSortField) => void
  onPageChange: (page: number) => void
  onSelect: (farm: AssignedFarm) => void
  isLoading: boolean
  isFetching?: boolean
  isError?: boolean
  onRetry: () => void
}

const formatLocality = (city: string | null, province: string | null): string => {
  if (city && province) return `${city}, ${province}`
  return city ?? province ?? '—'
}

// Colors match the prototype's --green/--yellow/--red tokens (plans/rpd_establecimientos_prototipo_localidad_segunda.html).
const CONTRACT_STATUS_BADGES: Record<
  Exclude<AssignedFarm['contractStatus'], null>,
  { color: string; backgroundColor: string }
> = {
  signed: { color: '#177a3f', backgroundColor: '#dff5e5' },
  terminating: { color: '#a76100', backgroundColor: '#fff0c9' },
  terminated: { color: '#b42318', backgroundColor: '#ffdede' },
}

const renderContractStatus = (status: AssignedFarm['contractStatus']): ReactNode => {
  const badge = status ? CONTRACT_STATUS_BADGES[status] : undefined
  if (!badge) return '—'
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        borderRadius: 999,
        px: 1.25,
        py: 0.5,
        fontSize: 13,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        color: badge.color,
        backgroundColor: badge.backgroundColor,
        '&::before': {
          content: '""',
          width: 9,
          height: 9,
          borderRadius: '50%',
          backgroundColor: 'currentColor',
        },
      }}
    >
      {CONTRACT_STATUS_LABELS[status]}
    </Box>
  )
}

const handleRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, onActivate: () => void) => {
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  onActivate()
}

// Measured from the live table: a header row, a body row, and the footer.
const TABLE_HEADER_HEIGHT = 58
const TABLE_ROW_HEIGHT = 53
const TABLE_FOOTER_HEIGHT = 64
const TABLE_MIN_HEIGHT_CAP = TABLE_HEADER_HEIGHT + 4 * TABLE_ROW_HEIGHT + TABLE_FOOTER_HEIGHT

// Hugs the row count so a short result leaves no blank card, capped for squeezed viewports.
export const computeTableMinHeight = (rowCount: number): number =>
  Math.min(
    TABLE_MIN_HEIGHT_CAP,
    TABLE_HEADER_HEIGHT + rowCount * TABLE_ROW_HEIGHT + TABLE_FOOTER_HEIGHT,
  )

const ELLIPSIS_CELL_SX = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const

// Fixed layout keeps columns from reflowing between pages and sorts; status fits the longest badge.
const COLUMN_WIDTHS = {
  name: '21%',
  locality: '23%',
  hub: '16%',
  program: '14%',
  status: '20%',
  chevron: '6%',
} as const
const TABLE_MIN_WIDTH = 960

const FarmsTable = ({
  farms,
  page,
  totalPages,
  total,
  pageSize,
  sort,
  queryKey,
  onToggleSort,
  onPageChange,
  onSelect,
  isLoading,
  isFetching = false,
  isError = false,
  onRetry,
}: FarmsTableProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0
  }, [queryKey])

  if (isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress aria-label="Cargando establecimientos" />
      </Stack>
    )
  }

  if (isError) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
        <Typography color="text.secondary">No pudimos cargar los establecimientos.</Typography>
        <Button variant="outlined" onClick={onRetry}>
          Reintentar
        </Button>
      </Stack>
    )
  }

  if (farms.length === 0) {
    return (
      <Stack alignItems="center" spacing={0.5} sx={{ py: 6, textAlign: 'center' }}>
        <Typography fontWeight="bold">No encontramos establecimientos</Typography>
        <Typography color="text.secondary">Probá modificando la búsqueda o los filtros.</Typography>
      </Stack>
    )
  }

  const [activeSortField, activeSortDirection] = sort.split(':') as [
    AssignedFarmsSortField,
    'asc' | 'desc',
  ]
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1
  const to = (page - 1) * pageSize + farms.length
  const establishmentWord = total === 1 ? 'establecimiento' : 'establecimientos'

  const sortableHeader = (field: AssignedFarmsSortField, label: ReactNode, width: string) => {
    const isActive = activeSortField === field
    return (
      <TableCell
        sortDirection={isActive ? activeSortDirection : false}
        sx={{ width, whiteSpace: 'nowrap' }}
      >
        <TableSortLabel
          active={isActive}
          direction={isActive ? activeSortDirection : 'asc'}
          onClick={() => onToggleSort(field)}
        >
          {label}
        </TableSortLabel>
      </TableCell>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: '0 1 auto',
        maxHeight: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {isFetching && <LinearProgress aria-label="Actualizando establecimientos" />}
      <TableContainer
        ref={scrollContainerRef}
        sx={{ flex: '0 1 auto', minHeight: 0, overflow: 'auto' }}
      >
        <Table
          stickyHeader
          aria-label="Establecimientos asignados"
          sx={{ minWidth: TABLE_MIN_WIDTH, tableLayout: 'fixed' }}
        >
          <TableHead sx={{ '& .MuiTableCell-stickyHeader': { backgroundColor: '#f7f9fb' } }}>
            <TableRow>
              {sortableHeader('name', <b>Establecimiento</b>, COLUMN_WIDTHS.name)}
              <TableCell sx={{ width: COLUMN_WIDTHS.locality, whiteSpace: 'nowrap' }}>
                Localidad
              </TableCell>
              {sortableHeader('hub', 'Nodo / Hub', COLUMN_WIDTHS.hub)}
              {sortableHeader('program', 'Programa', COLUMN_WIDTHS.program)}
              <TableCell sx={{ width: COLUMN_WIDTHS.status, whiteSpace: 'nowrap' }}>
                Estado contractual
              </TableCell>
              <TableCell sx={{ width: COLUMN_WIDTHS.chevron }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {farms.map(farm => (
              <TableRow
                key={farm.id}
                aria-label={`Ver establecimiento ${farm.name}`}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: '#fff',
                  '&:hover': { backgroundColor: '#f8fbff' },
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '-2px',
                    backgroundColor: '#f8fbff',
                  },
                }}
                tabIndex={0}
                onClick={() => onSelect(farm)}
                onKeyDown={event => handleRowKeyDown(event, () => onSelect(farm))}
              >
                <TableCell sx={ELLIPSIS_CELL_SX} title={farm.name}>
                  <b>{farm.name}</b>
                </TableCell>
                <TableCell sx={ELLIPSIS_CELL_SX} title={formatLocality(farm.city, farm.province)}>
                  {formatLocality(farm.city, farm.province)}
                </TableCell>
                <TableCell sx={ELLIPSIS_CELL_SX} title={farm.hub?.name ?? '—'}>
                  {farm.hub?.name ?? '—'}
                </TableCell>
                <TableCell sx={ELLIPSIS_CELL_SX} title={farm.program?.name ?? '—'}>
                  {farm.program?.name ?? '—'}
                </TableCell>
                <TableCell sx={ELLIPSIS_CELL_SX}>
                  {renderContractStatus(farm.contractStatus)}
                </TableCell>
                <TableCell align="right">›</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack
        alignItems="center"
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ p: 2, flexShrink: 0 }}
      >
        <Typography color="text.secondary" variant="body2">
          Mostrando {from}–{to} de {total} {establishmentWord}
        </Typography>
        <Pagination
          color="primary"
          count={totalPages}
          page={page}
          onChange={(_, value) => onPageChange(value)}
        />
      </Stack>
    </Box>
  )
}

export default FarmsTable
